import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tooltip,
} from '@mui/material';
import { ArrowRightIcon } from 'lucide-react';
import { Fragment, useState } from 'react';
import { CaseType } from '../../types/SimilarlySituated/CaseType.type.ts';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { canSaveDefineState, conditionBoxCount, MAX_CONDITION_BOXES } from '../../utils/ssbBuilderBox';
import { createEmptyCaseType, deriveCaseTypeName } from '../../utils/ssbCondition';
import { SSB_STEP_HEADERS } from '../../utils/ssbStepHeaders';
import { INSPECT_COLUMN_WIDTH } from '../../utils/ssbStyles';
import BoxWideTraitsCard from './BoxWideTraitsCard';
import CaseTypeCard from './CaseTypeCard';
import InspectCompanion from './InspectCompanion';
import SsbStepHeader from './SsbStepHeader';

interface DefineStepProps {
  caseTypes: CaseType[];
  boxTraits: SsbCondition[];
  countyId?: number | null;
  onCaseTypesChange: (caseTypes: CaseType[]) => void;
  onBoxTraitsChange: (traits: SsbCondition[]) => void;
  onBuildBox?: () => void;
  isBuildingBox?: boolean;
}

function DefineStep({
  caseTypes,
  boxTraits,
  countyId,
  onCaseTypesChange,
  onBoxTraitsChange,
  onBuildBox,
  isBuildingBox,
}: DefineStepProps) {
  const [deleteTarget, setDeleteTarget] = useState<CaseType | null>(null);

  const canAddCaseType = conditionBoxCount(caseTypes, boxTraits) < MAX_CONDITION_BOXES;
  const canAddTraits =
    caseTypes.some((caseType) => caseType.conditions.length > 0) &&
    (boxTraits.length > 0 || caseTypes.length < MAX_CONDITION_BOXES);

  const updateCaseType = (updated: CaseType) => {
    onCaseTypesChange(caseTypes.map((caseType) => (caseType.id === updated.id ? updated : caseType)));
  };

  const addCaseType = () => {
    onCaseTypesChange([...caseTypes, createEmptyCaseType()]);
  };

  const duplicateCaseType = (source: CaseType) => {
    const copy: CaseType = {
      id: crypto.randomUUID(),
      customName: source.customName ? `${source.customName} (copy)` : undefined,
      conditions: source.conditions.map((condition) => ({ ...condition, id: crypto.randomUUID() })),
      excepts: source.excepts.map((except) => ({ ...except, id: crypto.randomUUID() })),
    };
    const index = caseTypes.findIndex((caseType) => caseType.id === source.id);
    onCaseTypesChange([...caseTypes.slice(0, index + 1), copy, ...caseTypes.slice(index + 1)]);
  };

  const removeCaseType = (caseTypeId: string) => {
    onCaseTypesChange(caseTypes.filter((caseType) => caseType.id !== caseTypeId));
  };

  const handleRemoveRequest = (caseType: CaseType) => {
    // confirm delete only when case type card is not empty
    if (caseType.conditions.length > 0 || caseType.excepts.length > 0) {
      setDeleteTarget(caseType);
    } else {
      removeCaseType(caseType.id);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    removeCaseType(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ paddingBottom: '32px' }}>
      <SsbStepHeader {...SSB_STEP_HEADERS.Define} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {caseTypes.map((caseType, index) => (
          <Fragment key={caseType.id}>
            {index > 0 && (
              <Box sx={{ display: 'flex', gap: '20px' }}>
                <Divider
                  sx={{
                    flexGrow: 1,
                    alignSelf: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#718096',
                    letterSpacing: '0.06em',
                  }}
                >
                  OR
                </Divider>
                <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <CaseTypeCard
                  caseType={caseType}
                  countyId={countyId}
                  onChange={updateCaseType}
                  canDuplicate={canAddCaseType}
                  onDuplicate={() => duplicateCaseType(caseType)}
                  onRemove={() => handleRemoveRequest(caseType)}
                />
              </Box>
              <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }}>
                <InspectCompanion />
              </Box>
            </Box>
          </Fragment>
        ))}
        <Box sx={{ display: 'flex', gap: '20px' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tooltip title={canAddCaseType ? '' : `Maximum number of case types reached.`}>
              <span>
                <Button variant='outlined' disabled={!canAddCaseType} onClick={addCaseType}>
                  + Add case type (OR)
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: '20px', marginTop: '28px' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <BoxWideTraitsCard
              traits={boxTraits}
              canAddTraits={canAddTraits}
              countyId={countyId}
              onChange={onBoxTraitsChange}
            />
          </Box>
          <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: '20px', marginTop: '16px' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex' }}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              endIcon={<ArrowRightIcon size={16} />}
              disabled={!onBuildBox || isBuildingBox || !canSaveDefineState(caseTypes, boxTraits)}
              onClick={onBuildBox}
              sx={{ paddingX: 4 }}
            >
              Build
            </Button>
          </Box>
          <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }} />
        </Box>
      </Box>

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Delete case type?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Deleting &quot;{deleteTarget ? deriveCaseTypeName(deleteTarget) : ''}&quot; also deletes its conditions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant='contained' color='primary' disableElevation onClick={handleDeleteConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DefineStep;
